import React, { useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, Tooltip } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../../_actions/sidebarSliderActions";
import { ImageItemWithoutDrag } from "../../../_components/common/statelessView";
import SuperList from "../../../_components/details/sidebarPanels/mediaPanel/SuperList";
import { getFabricTypeFromMime } from "../../../_utils/common";
import {
  getAssetMessageFromAssetProcessingStatus,
  getCursorTypeFromAssetProcessingStatus,
} from "../../../_utils/mediaPanels";
import { AssetProcessingStatusTypes } from "../../../_utils/constants";

const AssetsList = ({ onItemClick, onItemDrag, disallowVideos }) => {
  const dispatch = useDispatch();
  const sidebarSlider = useSelector((state) => state.sidebarSlider);
  const { t } = useTranslation(["common"]);

  const handleItemClick = useCallback(
    (data) => {
      onItemClick?.(data);
    },
    [onItemClick]
  );

  const handleItemDrag = useCallback(
    (event, data) => {
      onItemDrag?.(event, data);
    },
    [onItemDrag]
  );

  const handleLoadPage = useCallback(
    (groupKey) => {
      const params = {
        panelType: sidebarSlider.sliderPanelType,
        page: sidebarSlider.page,
        searchText: sidebarSlider.searchText,
        groupKey,
      };

      dispatch(fetchData(sidebarSlider.source, params));
    },
    [dispatch, sidebarSlider]
  );

  const preparePayload = useCallback((source, data) => {
    data.thumbnail_width = data.thumbnail_width || 200;
    data.thumbnail_height = data.thumbnail_height || 200;

    const message = {
      mime: data.asset_type,
      type: data.asset_type === "shape" ? "icon" : data.asset_type,
      payload: {
        name: data.asset_key || "New Asset",
        src: data.thumbnail,
        cover: data?.thumbnail_cover_image,
        type: getFabricTypeFromMime(data.asset_type),
        height: data.thumbnail_height,
        width: data.thumbnail_width,
      },
      content: {
        meta: data,
        url: data.thumbnail,
      },
    };
    return message;
  }, []);

  const childElements = useMemo(() => {
    return sidebarSlider.payload.map((itemData) => {
      const itemPayload = preparePayload(sidebarSlider.source, itemData);
      const allowedType = itemPayload.mime !== "video" || !disallowVideos;
      const cursor = getCursorTypeFromAssetProcessingStatus(itemData.status);
      const errorMessage = getAssetMessageFromAssetProcessingStatus(
        itemData.status
      );
      const isActionAllowed = [
        AssetProcessingStatusTypes.DONE,
        AssetProcessingStatusTypes.THUMBNAIL_FAILED,
      ].includes(itemData.status) && allowedType;

      return (
        <Tooltip
          hasArrow
          placement="top"
          label={t(errorMessage, { ns: "common" })}
          isDisabled={!errorMessage}
          key={itemData.id}
        >
          <Box
            className="item"
            onClick={() => {
              if (onItemClick && isActionAllowed) handleItemClick(itemPayload);
            }}
            onDragStart={
              onItemDrag
                ? (event) => {
                    if (isActionAllowed) {
                      handleItemDrag(event, itemPayload);
                    } else {
                      event.preventDefault();
                    }
                  }
                : null
            }
            draggable={!!onItemDrag && isActionAllowed}
            data-grid-groupkey={itemData.groupKey}
            cursor={isActionAllowed ? cursor : "initial"}
          >
            <ImageItemWithoutDrag
              data={itemPayload}
              key={itemData.id}
              url={
                itemData.thumbnail_cover_image
                  ? itemData.thumbnail_cover_image
                  : itemData.thumbnail
              }
              alt={itemPayload.payload.src}
              width={itemData.cover_image_width || 200}
              height={itemData.cover_image_height || 200}
              source={"TLDR"}
              showOverlayInfo={false}
            />
            {!allowedType && (
              <Box
                position="absolute"
                inset="0"
                display="flex"
                alignItems="center"
                justifyContent="center"
                background="blackAlpha.600"
                borderRadius="md"
                color="gray.100"
                fontSize="sm"
                textAlign="center"
                padding="4"
              >
                {t("file_type_not_supported")}
              </Box>
            )}
          </Box>
        </Tooltip>
      );
    });
  }, [
    sidebarSlider.source,
    sidebarSlider.payload,
    preparePayload,
    onItemClick,
    onItemDrag,
    handleItemClick,
    handleItemDrag,
    t,
    disallowVideos
  ]);

  return (
    <>
      <SuperList
        list="sm-post-media-panel assets-gallery"
        loaded={sidebarSlider.loaded}
        searchPlaceholder={"Search assets"}
        notFound={"There are no assets."}
        errorsView={null}
        hasMore={!!sidebarSlider.hasMore}
        loadPage={handleLoadPage}
        onClickRecent={(itemPayload) => {
          handleItemClick(itemPayload);
        }}
        onDragRecent={
          onItemDrag ? (event, data) => handleItemDrag(event, data) : null
        }
        recentsWithoutDragAndDropContext
      >
        {childElements}
      </SuperList>
    </>
  );
};

export default AssetsList;
